'use client';

import React, { RefObject } from 'react';
import { trailingThrottle } from '@benzinga/utils';
import { useEventListener, UseEventListnerOptions } from './useEventListener';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

interface ScrollInfo {
  bottom: number;
  hasScrolled: boolean;
  percentageScrolled: number;
  top: number;
}

export const getScrollPosition = (element?: HTMLElement): number => {
  if (element) {
    return element.scrollTop;
  }
  return window.scrollY || document.documentElement.scrollTop;
};

export const getScrollTop = (element?: HTMLElement): number => {
  if (element) {
    return element.scrollTop;
  }
  return (document.scrollingElement || document.documentElement).scrollTop;
};

export const getScrollBottom = (element?: HTMLElement): number => {
  if (element) {
    return Math.abs(element.clientHeight + element.scrollTop - element.scrollHeight);
  }
  return Math.abs(window.innerHeight + getScrollPosition() - document.body.scrollHeight);
};

export const getPageScrollPercentage = (element?: HTMLElement): number => {
  if (element) {
    const scrollPosition = element.scrollTop;
    const scrollHeight = element.scrollHeight - element.clientHeight;
    return (scrollPosition / scrollHeight || 0) * 100;
  }

  const scrollPosition = getScrollPosition();
  const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  return (scrollPosition / scrollHeight || 0) * 100;
};

export function useScrollInfo(): ScrollInfo;

export function useScrollInfo<T extends HTMLElement>(
  elementRef: RefObject<T>,
  maxWait?: number,
  scrollEventListenerOptions?: UseEventListnerOptions,
): ScrollInfo;

export function useScrollInfo<T extends HTMLElement>(
  elementRef?: RefObject<T>,
  maxWait = 100,
  scrollEventListenerOptions?: UseEventListnerOptions,
): ScrollInfo {
  const [scrollInfo, setScrollInfo] = React.useState<ScrollInfo>({
    bottom: 0,
    hasScrolled: false,
    percentageScrolled: 0,
    top: 0,
  });

  const handleScroll = React.useCallback(() => {
    const element = elementRef?.current as HTMLElement;
    const newScrollInfo: ScrollInfo = {
      bottom: getScrollBottom(element),
      hasScrolled: true,
      percentageScrolled: getPageScrollPercentage(element),
      top: getScrollTop(element),
    };
    setScrollInfo(newScrollInfo);
  }, [elementRef]);

  const scrollListener = React.useMemo(() => trailingThrottle(handleScroll, maxWait), [handleScroll, maxWait]);

  useEventListener('scroll', scrollListener, elementRef as RefObject<T>, scrollEventListenerOptions);

  useEventListener('resize', scrollListener, elementRef as RefObject<T>, scrollEventListenerOptions);

  useIsomorphicLayoutEffect(() => {
    scrollListener();
  }, [scrollListener]);

  return scrollInfo;
}

export default useScrollInfo;
